import { getAssetStreamUrl, getAssetThumbnailUrl } from '@/lib/api/media-proxy';
import { toDisplayMediaUrl } from '@/lib/media-url';

const ASSET_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EXPIRY_SKEW_MS = 30_000;

export function isDurableAssetId(value?: string | null): value is string {
  return !!value && ASSET_ID_RE.test(value);
}

export function isUsableMediaUrl(value?: string | null): value is string {
  if (!value) return false;
  const url = value.trim();
  return /^https?:\/\//i.test(url)
    || /^\/api\/(?:auth\/media\/)?assets\/(?:stream\/[0-9a-f-]{36}|[0-9a-f-]{36}\/thumbnail)(?:[?#]|$)/i.test(url);
}

function signedTimestamp(value: string | null): number | null {
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(value || '');
  if (!match) return null;
  const timestamp = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]),
    Number(match[4]), Number(match[5]), Number(match[6]));
  return Number.isFinite(timestamp) ? timestamp : null;
}

/** Known provider credentials must never be treated as durable media addresses. */
function signedExpiry(url: string): number | null {
  try {
    const params = new URL(url, 'https://dobidobi.local').searchParams;
    const googleIssued = signedTimestamp(params.get('X-Goog-Date'));
    const googleLifetime = Number(params.get('X-Goog-Expires'));
    if (googleIssued !== null && params.has('X-Goog-Expires') && Number.isFinite(googleLifetime) && googleLifetime >= 0) {
      return googleIssued + googleLifetime * 1000;
    }
    const awsIssued = signedTimestamp(params.get('X-Amz-Date'));
    const awsLifetime = Number(params.get('X-Amz-Expires'));
    if (awsIssued !== null && params.has('X-Amz-Expires') && Number.isFinite(awsLifetime) && awsLifetime >= 0) {
      return awsIssued + awsLifetime * 1000;
    }
    const cosEnd = Number((params.get('q-sign-time') || '').split(';')[1]);
    if (params.has('q-sign-time') && Number.isFinite(cosEnd) && cosEnd > 0) {
      return cosEnd * 1000;
    }
  } catch {
    // Unknown URL schemes keep their current display behavior.
  }
  return null;
}

export function isExpiredSignedMediaUrl(url: string, now: number = Date.now()): boolean {
  const expiresAt = signedExpiry(url);
  return expiresAt !== null && expiresAt <= now + EXPIRY_SKEW_MS;
}

export function isTemporaryMediaUrl(url: string): boolean {
  try {
    const params = new URL(url, 'https://dobidobi.local').searchParams;
    return [...params.keys()].some((key) => /^(x-goog-|x-amz-|q-sign-|q-signature$|media_token$)/i.test(key));
  } catch {
    return false;
  }
}

export function shouldUseDurableMediaUrl(url: string): boolean {
  return isTemporaryMediaUrl(url) && (signedExpiry(url) === null || isExpiredSignedMediaUrl(url));
}

export function getAssetBackedImageSources(
  urls: Array<string | null | undefined>, assetId?: string | null,
): string[] {
  const durable = isDurableAssetId(assetId) ? getAssetStreamUrl(assetId) : '';
  const live = urls.map((url) => url?.trim() || '')
    .filter((url) => url && (!durable || !shouldUseDurableMediaUrl(url)))
    .map(toDisplayMediaUrl);
  return [...new Set([...live, durable].filter(Boolean))];
}

export function getAssetBackedVideoPosterUrl(url: string | null | undefined, assetId?: string | null): string {
  const direct = url?.trim() || '';
  if (direct && (!isDurableAssetId(assetId) || !shouldUseDurableMediaUrl(direct))) return toDisplayMediaUrl(direct);
  return isDurableAssetId(assetId) ? getAssetThumbnailUrl(assetId) : direct;
}
